import React, { useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import Modal from '../../components/Modal';
import Uploader from '../../components/Uploader';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import type { ImportExtension } from '../../graphql/generated/graphql';
import { ImportSource } from '../../graphql/generated/graphql';
import { adminToasts } from '../../toasts/toasts';
import type { BaseModalProps } from '../../ui/uiSlice';
import { UIActions } from '../../ui/uiSlice';
import { useBeforeLeaveConfirm } from '../../utils/BeforeLeaveConfirm';
import Logger from '../../utils/Logger';
import getExtname from '../../utils/url/getExtname';
import BucketUpload from './BucketUpload';
import * as styles from './ImportFileModal.module.scss';
import ImportFileProgress from './ImportFileProgress';
import ImportFilesConfirmation from './ImportFilesConfirmation';

const IMPORT_FILES_MUTATION = gql(/** @complexity 8 */ `
  mutation importFiles($files: [Upload!]!, $source: ImportSource) {
    importedFiles: importFiles(files: $files, source: $source) {
      ...ImportFilesConfirmation_importedFiles
    }
  }
`);

const GENERATE_IMPORT_UPLOAD_URL_MUTATION = gql(/** @complexity 2 */ `
  mutation generateImportUploadUrl(
    $extension: ImportExtension!
    $source: ImportSource!
  ) {
    url: generateImportUploadUrl(extension: $extension, source: $source)
  }
`);

const COMPLETE_IMPORT_MUTATION = gql(/** @complexity 2 */ `
  mutation completeUploadedImport($url: String!, $byteSize: Int!) {
    completeUploadedImport(url: $url, byteSize: $byteSize)
  }
`);

const BACKEND_TIMEOUT_FOR_OFFLINE_PROCESSING = 20 * 1000;

const useDirectUpload = ({
  offlineProcessingTimeout,
  onError,
}: {
  offlineProcessingTimeout: number;
  onError: () => unknown;
}) => {
  const [importFiles, { data, loading }] = useMutation(IMPORT_FILES_MUTATION);
  const [isOfflineProcessing, setIsOfflineProcessing] = useState(false);
  const fileSizeRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (loading) {
      setIsOfflineProcessing(false);

      const timeout = setTimeout(() => {
        // If there is truly nothing to import, the backend should respond quickly
        // If we are waiting for a long time, it likely means backend is switching to
        // offline processing
        setIsOfflineProcessing(true);
        Logger.message('Detected offline import processing', {
          size: fileSizeRef.current,
        });
      }, offlineProcessingTimeout);

      return () => clearTimeout(timeout);
    }

    return undefined;
  }, [loading, offlineProcessingTimeout]);

  const handleUpload = async (file: File, source: ImportSource) => {
    try {
      fileSizeRef.current = file.size;
      await importFiles({ variables: { files: [file], source } });
    } catch (e) {
      setIsOfflineProcessing(false);
      onError();
    }
  };

  const result = isOfflineProcessing ? 'OFFLINE' : data?.importedFiles;

  return [handleUpload, result] as const;
};

const useBucketUpload = ({ onError }: { onError: () => unknown }) => {
  const [progress, setProgress] = useState(0);
  const [generateImportUploadUrl] = useMutation(
    GENERATE_IMPORT_UPLOAD_URL_MUTATION,
  );
  const [completeImport] = useMutation(COMPLETE_IMPORT_MUTATION);
  const [currentUpload, setCurrentUpload] = useState<BucketUpload | null>(null);

  useBeforeLeaveConfirm({
    skip: progress <= 0 || progress >= 100,
    message: 'Do you want to stop uploading?',
  });

  const handleError = () => {
    setProgress(0);
    setCurrentUpload(null);
    onError();
  };

  const prepareUpload = async (file: File, source: ImportSource) => {
    const extension = getExtname(file.name).toUpperCase() as ImportExtension;
    try {
      const { data } = await generateImportUploadUrl({
        variables: { extension, source },
      });

      return data?.url
        ? new BucketUpload({
            chunkSize: 5 * 1024 * 1024, // 5MB
            file,
            onProgress: ({ loaded, total }) => {
              // Leave 1% for the complete upload call
              setProgress(Math.ceil((loaded / total) * 99));
            },
            url: data.url,
          })
        : null;
    } catch (e) {
      handleError();
      return null;
    }
  };

  const performUpload = async (upload: BucketUpload) => {
    try {
      await upload.start();
      await completeImport({
        variables: { url: upload.getUrl(), byteSize: upload.getFileSize() },
      });
      setProgress(100);
    } catch (error) {
      Logger.error(error);
      handleError();
    }
  };

  useEffect(() => {
    return () => {
      currentUpload?.cancel();
    };
  }, [currentUpload]);

  const bucketUpload = async (file: File, source: ImportSource) => {
    currentUpload?.cancel();
    const upload = await prepareUpload(file, source);

    if (upload) {
      setCurrentUpload(upload);
      performUpload(upload);
    }
  };

  return [
    bucketUpload,
    currentUpload
      ? {
          progress,
        }
      : undefined,
  ] as const;
};

const MODAL_WIDTH = 425;
const MODAL_TITLE = 'Import Files';

interface BaseImportFileModalProps extends BaseModalProps {
  footer?: React.ReactNode;
  handleDrop: (files: File[]) => Promise<void>;
  uploaderInfo?: React.ComponentProps<typeof Uploader>['info'];
  bucketUploadData?: { progress: number };
  directUploadData?:
    | 'OFFLINE'
    | DocumentType<typeof IMPORT_FILES_MUTATION>['importedFiles'];
}

// eslint-disable-next-line import/no-unused-modules -- expose for storybook
export const BaseImportFileModal = ({
  close,
  handleDrop,
  uploaderInfo,
  footer,
  bucketUploadData,
  directUploadData,
}: BaseImportFileModalProps) => {
  const sharedProps: React.ComponentProps<typeof Modal> = {
    title: MODAL_TITLE,
    close,
    variant: 'expanded',
    width: MODAL_WIDTH,
  };

  if (bucketUploadData) {
    const { progress } = bucketUploadData;

    if (progress >= 100) {
      return (
        <Modal
          {...sharedProps}
          description="Your file has been successfully uploaded. We'll continue the import process in the background, and send you an email once it's complete. Meanwhile, feel free to close this modal."
        />
      );
    }

    return (
      <Modal {...sharedProps}>
        <ImportFileProgress message="Uploading…" progress={progress} />
      </Modal>
    );
  }

  if (directUploadData === 'OFFLINE') {
    return (
      <Modal
        {...sharedProps}
        description="Large import detected. We'll continue the import process in the background. Feel free to close this modal and return to Slab later."
      />
    );
  }

  if (directUploadData) {
    return (
      <Modal
        {...sharedProps}
        description={
          <ImportFilesConfirmation importedFiles={directUploadData} />
        }
      />
    );
  }

  return (
    <Modal {...sharedProps}>
      <Uploader
        accept={IMPORT_ARCHIVE_ACCEPT}
        type="file"
        onDrop={handleDrop}
        info={uploaderInfo}
      />
      {footer ? <div className={styles.footer}>{footer}</div> : null}
    </Modal>
  );
};

const IMPORT_ARCHIVE_ACCEPT = {
  'application/zip': ['.zip'],
};

interface ImportFileModalProps
  extends Pick<BaseImportFileModalProps, 'footer' | 'uploaderInfo'>,
    BaseModalProps {
  forceBucketUpload?: boolean;
  offlineProcessingTimeout?: number;
  source?: ImportSource;
}

const ImportFileModal = ({
  close,
  footer,
  forceBucketUpload,
  offlineProcessingTimeout = BACKEND_TIMEOUT_FOR_OFFLINE_PROCESSING,
  source = ImportSource.File,
  uploaderInfo = 'Upload a zip file containing your HTML and Markdown files',
}: ImportFileModalProps) => {
  const dispatch = useAppDispatch();
  const handleError = () =>
    dispatch(UIActions.pushToast(adminToasts.importError));

  const [directUpload, directUploadData] = useDirectUpload({
    offlineProcessingTimeout,
    onError: handleError,
  });
  const [bucketUpload, bucketUploadData] = useBucketUpload({
    onError: handleError,
  });

  const handleDrop = async ([file]: File[]) => {
    if (!file) return;

    if (
      file.size > window.__slab.MAX_IMPORT_MB * 1000 * 1000 ||
      forceBucketUpload
    ) {
      await bucketUpload(file, source);
    } else {
      await directUpload(file, source);
    }
  };

  return (
    <BaseImportFileModal
      close={close}
      handleDrop={handleDrop}
      uploaderInfo={uploaderInfo}
      footer={footer}
      bucketUploadData={bucketUploadData}
      directUploadData={directUploadData}
    />
  );
};

export default ImportFileModal;
